import { EuiBadge, EuiLoadingSpinner } from "@inscopix/ideas-eui";
import { useCallback, useEffect, useState } from "react";
import { Prompt } from "react-router-dom";
import {
  useDataTableContext,
  useRefetchDataTableStore,
} from "../store/DataTableProvider";
import { useModalContext } from "providers/ModalProvider/ModalProvider";
import { ModalProjectSyncError } from "components/ModalProjectSyncError/ModalProjectSyncError";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";

/**
 * Component that renders a badge to indicate whether the project is syncing.
 *
 * This component all handles displaying a browser prompt when the user tries
 * to leaves while changes are still being persisted to the server.
 */
export const DataTableSyncStatusBadge = () => {
  const { openModal, closeModal } = useModalContext();

  const syncStatus = useDataTableContext((s) => s.syncStatus);
  const [isPromptVisible, setIsPromptVisible] = useState(false);

  const selectedTableKey = useDataTableContext((s) =>
    s.tables.find((t) => t.id === s.selectedTableId),
  )?.key;

  const refetchDataTableStore = useRefetchDataTableStore();

  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = "";
  }, []);

  useEffect(() => {
    // only stop the user from leaving the page if the sync status is "syncing"
    // (we'll show a modal if the status is "error", and then we do expect them to refresh)
    if (syncStatus.status === "syncing") {
      window.addEventListener("beforeunload", handleBeforeUnload);
      setIsPromptVisible(true);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      setIsPromptVisible(false);
    }

    if (syncStatus.status === "error") {
      openModal(<ModalProjectSyncError onClose={closeModal} />);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [closeModal, handleBeforeUnload, openModal, syncStatus.status]);

  const { color, icon, statusText } = (() => {
    switch (syncStatus.status) {
      case "idle":
        return {
          color: "hollow",
          icon: "check",
          statusText: "Changes synced",
        };
      case "syncing":
        return {
          color: "hollow",
          icon: () => <EuiLoadingSpinner size="s" style={{ marginRight: 4 }} />,
          statusText: "Syncing changes...",
        };
      case "error":
        return {
          color: "danger",
          icon: "error",
          statusText: "Error syncing changes",
        };
    }
  })();

  const onRefresh = () => {
    refetchDataTableStore(selectedTableKey).catch(() =>
      addUtilityToastFailure("Failed to refresh."),
    );
  };

  return (
    <div>
      <Prompt
        when={isPromptVisible}
        message="Leaving the page will erase unsaved changes. Continue anyway?"
      />
      <EuiBadge color={color} iconType={icon} id="data-table-sync-status-badge">
        {statusText}
      </EuiBadge>
      {syncStatus.status === "error" && (
        <EuiBadge
          color="warning"
          iconType="refresh"
          id="data-table-sync-revert-badge"
          onClick={() => {
            onRefresh();
            closeModal();
          }}
          onClickAriaLabel="Revert project to last saved changes"
          iconOnClick={() => {
            onRefresh();
            closeModal();
          }}
          iconOnClickAriaLabel="Revert project to last saved changes"
        />
      )}
    </div>
  );
};
